import { styled } from '@mui/material/styles';
import * as React from 'react';
import { Button, Link as MuiLink } from '@mui/material';
import { PaymentOutlined, PlayArrowOutlined, RedeemOutlined, RemoveRedEyeOutlined } from '@mui/icons-material';
import { BottomBar } from '@lialo/pwa-ui/lib/PageComponents/BottomBar';
import { useTranslation } from 'react-i18next';
import routes from '@lialo/core/lib/pages/utils/routes';
import { Link } from 'react-router-dom';
import { PaymentMethodWrapper } from '@lialo/payment/lib/components/PaymentMethodWrapper';
import { NextButton } from './NextButton';
import { isIOSApp } from '../../../services/utils';
import { useServiceProvider } from '../../../common/Providers';

const PREFIX = 'BottomActionBar';

const classes = {
  toolBar: `${PREFIX}-toolBar`,
  grow: `${PREFIX}-grow`,
  purchaseCoupon: `${PREFIX}-purchaseCoupon`,
};

const StyledBottomBar = styled(BottomBar)(({ theme }) => ({
  [`& .${classes.toolBar}`]: {
    minHeight: 'inherit',
    padding: theme.spacing(1),
  },

  [`& .${classes.grow}`]: {
    flexGrow: 1,
  },

  [`& .${classes.purchaseCoupon}`]: {
    marginRight: theme.spacing(1),
  },
}));

interface IProps {
  tourCode: string;
  hasInsight: boolean;

  // tour has a price and is not yet purchased
  requiresPurchase: boolean;
  // tour has a price
  couponAvailable: boolean;
}

const BottomActionBar = ({ tourCode, requiresPurchase, hasInsight, couponAvailable }: IProps) => {
  const { t } = useTranslation();
  const isInIOSApp = isIOSApp();
  const { navigationService } = useServiceProvider();

  const purchaseCouponButton = couponAvailable ? (
    <Button
      className={classes.purchaseCoupon}
      color={'primary'}
      onClick={() => navigationService.navigate(navigationService.generatePurchaseUnlockCouponUrl(tourCode))}
      startIcon={<RedeemOutlined />}
    >
      {t('tour_detail.bottom_actions.purchase_coupon')}
    </Button>
  ) : null;

  return (
    <StyledBottomBar elevation={2} toolbarClassName={classes.toolBar}>
      <>
        {hasInsight && (
          <Button
            component={MuiLink}
            href={'#insights'}
            size={'small'}
            color={'primary'}
            variant={'text'}
            startIcon={<RemoveRedEyeOutlined />}
          >
            {t('tour_detail.bottom_actions.tour_insight')}
          </Button>
        )}

        <div className={classes.grow} />
        {purchaseCouponButton}

        {requiresPurchase ? (
          <PaymentMethodWrapper>
            <NextButton
              to={navigationService.generateTourCheckoutUrl(
                tourCode,
                isInIOSApp
                  ? {
                      utm_source: 'ios_app',
                      utm_medium: 'button_checkout',
                      utm_campaign: tourCode,
                    }
                  : undefined
              )}
              startIcon={<PaymentOutlined />}
            >
              {t('tour_detail.bottom_actions.to_checkout')}
            </NextButton>
          </PaymentMethodWrapper>
        ) : (
          <NextButton
            to={navigationService.generatePlayTourUrl(
              routes.PLAY_TOUR,
              tourCode,
              true,
              isInIOSApp
                ? {
                    utm_source: 'ios_app',
                    utm_medium: 'button_start',
                    utm_campaign: tourCode,
                  }
                : undefined
            )}
            startIcon={<PlayArrowOutlined />}
          >
            {t('actions.start')}
          </NextButton>
        )}
      </>
    </StyledBottomBar>
  );
};

export default BottomActionBar;
